/**
 * NotificationBell — top-bar 🔔 with an unread badge and a dropdown panel.
 *
 * Reads the current user's notifications, subscribes to realtime SSE
 * (`notification.new`), and falls back to a 60s poll + tab-focus refresh.
 * Clicking a notification marks it read and navigates to its deep link.
 * Channels are in-app + email only — this is the in-app surface.
 */
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell, Package, FileText, BarChart3, TrendingUp, Landmark,
  CheckCircle2, Target, CheckCheck, Settings2, Wrench,
} from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
  listNotifications, markNotificationRead, markAllNotificationsRead,
} from '@/api/notifications';
import { subscribeNotifications } from '@/api/notificationsStream';

const CAT_ICON = {
  material_requests: Package,
  change_orders: FileText,
  reports: BarChart3,
  kpi_alerts: TrendingUp,
  permits: Landmark,
  workflows: CheckCircle2,
  leads: Target,
  equipment_subs_tracking: Wrench,
};

// LOOP W3 — why a bell item has no live link.
const TARGET_REASON = {
  gone: 'The item this refers to no longer exists.',
  gated: "This section isn't turned on for your workspace.",
};

function timeAgo(iso) {
  if (!iso) return '';
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)}m`;
  if (s < 86400) return `${Math.round(s / 3600)}h`;
  return `${Math.round(s / 86400)}d`;
}

export function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async (opts) => {
    try {
      const { data } = await listNotifications({ limit: 15 }, opts);
      setItems(data.notifications || []);
      setUnread(data.unread || 0);
    } catch { /* stay quiet */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Realtime — server pre-filters to this user via the recipients allow-list.
  useEffect(() => subscribeNotifications((evt) => {
    if (evt.type === 'notification.new' && evt.notification) {
      setItems((cur) => (cur.some((x) => x.id === evt.notification.id)
        ? cur : [evt.notification, ...cur].slice(0, 20)));
      setUnread((u) => u + 1);
    }
  }), []);

  // Poll fallback + refresh on tab-focus (covers any SSE gap). The
  // interval is visibility-gated and tagged `poll:notifications` so the
  // backend usage tracker (LOOP B3.1) does NOT count it as engagement;
  // the tab-focus refresh stays UNTAGGED — returning to the tab is a real
  // interaction and should keep counting.
  useEffect(() => {
    const onVis = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onVis);
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') load({ pollTag: 'notifications' });
    }, 60000);
    return () => { document.removeEventListener('visibilitychange', onVis); clearInterval(id); };
  }, [load]);

  const onItemClick = async (n) => {
    if (!n.read) {
      setItems((cur) => cur.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      setUnread((u) => Math.max(0, u - 1));
      try { await markNotificationRead(n.id); } catch { /* noop */ }
    }
    // LOOP W3 — navigate only when there's a reachable link; otherwise open a
    // detail modal (dead/gated/hrefless target) instead of a broken link.
    const href = n.href || n.link;
    if (href && n.target_ok !== false) {
      setOpen(false);
      navigate(href);
    } else {
      setOpen(false);
      setDetail(n);
    }
  };

  const onMarkAll = async () => {
    setItems((cur) => cur.map((x) => ({ ...x, read: true })));
    setUnread(0);
    try { await markAllNotificationsRead(); } catch { /* noop */ }
  };

  return (
    <>
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="notification-bell-button"
          aria-label="Notifications"
          className="relative flex items-center justify-center w-8 h-8 rounded-md transition-colors hover:bg-[var(--strata-canvas-soft)]"
          style={{ color: 'var(--strata-text-secondary)' }}
        >
          <Bell className="w-4 h-4" />
          {unread > 0 && (
            <span
              data-testid="notification-badge"
              className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full flex items-center justify-center text-[10px] font-bold text-white"
              style={{ background: '#B85C5C', fontFamily: 'Montserrat, sans-serif' }}
            >
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-[360px] p-0 overflow-hidden"
        data-testid="notification-panel"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: 'var(--strata-border)' }}>
          <span className="text-sm font-semibold" style={{ color: 'var(--strata-navy)', fontFamily: 'Outfit' }}>
            Notifications
          </span>
          {unread > 0 && (
            <button
              type="button"
              data-testid="notification-mark-all"
              onClick={onMarkAll}
              className="flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded transition-colors hover:bg-[var(--strata-canvas-soft)]"
              style={{ color: 'var(--strata-steel)' }}
            >
              <CheckCheck className="w-3 h-3" /> Mark all read
            </button>
          )}
        </div>

        <div className="max-h-[380px] overflow-y-auto">
          {items.length === 0 ? (
            <div data-testid="notification-empty" className="px-4 py-10 text-center">
              <Bell className="w-6 h-6 mx-auto mb-2 opacity-30" />
              <p className="text-xs text-muted-foreground">You're all caught up.</p>
            </div>
          ) : (
            items.map((n) => {
              const Icon = CAT_ICON[n.category] || Bell;
              return (
                <button
                  key={n.id}
                  type="button"
                  data-testid={`notification-item-${n.id}`}
                  onClick={() => onItemClick(n)}
                  className="w-full flex gap-3 px-4 py-3 text-left border-b transition-colors hover:bg-[var(--strata-canvas-soft)]"
                  style={{ borderColor: 'var(--strata-border)', background: n.read ? 'transparent' : 'rgba(69,117,168,0.06)' }}
                >
                  <span
                    className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full mt-0.5"
                    style={{ background: 'rgba(69,117,168,0.12)', color: 'var(--strata-steel)' }}
                  >
                    <Icon className="w-4 h-4" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="text-[13px] font-semibold truncate" style={{ color: 'var(--strata-navy)' }}>
                        {n.title}
                      </span>
                      <span className="text-[10px] flex-shrink-0" style={{ color: 'var(--strata-text-muted)' }}>
                        {timeAgo(n.created_at)}
                      </span>
                    </div>
                    {n.body && (
                      <p className="text-[12px] mt-0.5 line-clamp-2" style={{ color: 'var(--strata-text-secondary)', whiteSpace: 'pre-wrap' }}>
                        {n.body}
                      </p>
                    )}
                  </div>
                  {!n.read && (
                    <span className="flex-shrink-0 w-2 h-2 rounded-full mt-1.5" style={{ background: '#4575A8' }} />
                  )}
                </button>
              );
            })
          )}
        </div>

        <button
          type="button"
          data-testid="notification-settings-link"
          onClick={() => { setOpen(false); navigate('/settings?tab=notifications'); }}
          className="w-full flex items-center justify-center gap-1.5 px-4 py-2.5 text-[11px] font-medium border-t transition-colors hover:bg-[var(--strata-canvas-soft)]"
          style={{ borderColor: 'var(--strata-border)', color: 'var(--strata-text-secondary)' }}
        >
          <Settings2 className="w-3 h-3" /> Notification settings
        </button>
      </PopoverContent>
    </Popover>

    <Dialog open={!!detail} onOpenChange={(o) => { if (!o) setDetail(null); }}>
      <DialogContent className="max-w-md" data-testid="notification-detail-modal">
        <DialogHeader>
          <DialogTitle data-testid="notification-detail-title">{detail?.title}</DialogTitle>
          <DialogDescription className="text-[11px] uppercase tracking-wide">
            {detail?.category} · {timeAgo(detail?.created_at)} ago
          </DialogDescription>
        </DialogHeader>
        {detail?.body && (
          <p className="text-sm whitespace-pre-wrap" style={{ color: 'var(--strata-text-secondary)' }} data-testid="notification-detail-body">
            {detail.body}
          </p>
        )}
        {detail && detail.target_ok === false && (
          <p className="text-xs rounded-md px-3 py-2 mt-1" style={{ background: 'rgba(184,92,92,0.08)', color: '#8A4B4B' }} data-testid="notification-detail-reason">
            {TARGET_REASON[detail.target_scope] || 'This notification has no linked page.'}
          </p>
        )}
      </DialogContent>
    </Dialog>
    </>
  );
}
